/**
 * The left sidebar's widths, kept in a leaf module.
 *
 * These live apart from the panel component because the split layout needs both
 * widths to size its column, and importing them from the component would pull
 * that component's whole graph — panel → new-chat dialog → Omnibox → lottie —
 * into everything downstream of the layout, including `lib/clearClientState.ts`.
 * A value import loads the module it comes from, so "just two numbers" is never
 * just two numbers.
 */

/**
 * Where the sidebar rests before the user drags it.
 *
 * The list is one column of thread titles, so the width is set by how much of a
 * title reads before it truncates rather than by anything it has to fit whole.
 * 380 gives a title's first several words, and matches where the canvas column
 * rests on the other side of the screen: the two side columns open at one width,
 * so neither reads as the odd one out.
 *
 * Inside `[SIDEBAR_MIN_WIDTH, SIDEBAR_MAX_WIDTH]`, deliberately: a resting width
 * outside its own drag range is one the user can never drag back to, reachable
 * again only by double-clicking the divider.
 */
export const SIDEBAR_WIDTH = 380;

/**
 * How narrow the sidebar can be dragged.
 *
 * 260 is about where comparable chat sidebars sit doing the same job, one column
 * of conversation titles, and it still shows enough of a title to tell two
 * threads apart. Below the shared
 * `DEFAULT_PANEL_MIN_WIDTH` on purpose, because that floor is sized for a panel
 * showing a page, and this one is showing a list.
 */
export const SIDEBAR_MIN_WIDTH = 260;

/**
 * How wide the sidebar can be dragged — the same as where it rests, so the
 * drag only ever narrows it.
 *
 * The resting width is already as much of a title as this list needs; past it
 * the sidebar takes screen the canvas wants without reading any more of the
 * titles it holds. So the range runs downward instead: 260 to 380 is 120px of
 * travel, for the user who wants the list out of the way rather than bigger.
 */
export const SIDEBAR_MAX_WIDTH = 380;
